import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ActivityStrip } from "@/components/profile/ActivityStrip";
import { AccountsSection } from "@/components/profile/AccountsSection";

export default function ProfilePage() {
  const { handle } = useParams();
  const { user, refresh } = useAuth();
  const isOwn = !handle || handle === user?.handle;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ handle: "", bio: "", is_public: true });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      const target = handle || user?.handle;
      if (!target) return;
      try {
        const { data } = await api.get(`/public/profile/${target}`);
        setProfile(data);
      } catch (e) {
        setProfile({ error: e.response?.data?.detail || "Not found" });
      }
      setLoading(false);
    })();
  }, [handle, user?.handle]);

  useEffect(() => {
    if (isOwn && user) {
      setForm({
        handle: user.handle || "",
        bio: user.bio || "",
        is_public: user.is_public !== false,
      });
    }
  }, [user, isOwn]);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await api.patch("/auth/profile", {
        handle: form.handle,
        bio: form.bio,
        is_public: form.is_public,
      });
      await refresh();
      setMsg("Profile saved.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <Layout>
      <div className="max-w-3xl fade-in">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#CFA66A] mb-2">— Trader profile</p>
        {loading ? (
          <p className="text-[#A39A8E] font-mono text-sm">Loading…</p>
        ) : profile?.error ? (
          <p className="text-[#B56258] font-mono">{profile.error}</p>
        ) : profile && (
          <>
            <h1 className="font-head text-4xl sm:text-5xl font-bold text-[#EBE5D9]">{profile.trader.name}</h1>
            <p className="font-mono text-sm text-[#A39A8E]">@{profile.trader.handle} · since {profile.trader.member_since}</p>
            {profile.trader.bio && <p className="mt-4 text-[#EBE5D9] max-w-2xl">{profile.trader.bio}</p>}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
              <div className="border border-[#2E2A25] p-4 rounded-sm">
                <p className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">Total P&amp;L</p>
                <p className={`font-mono text-xl mt-1 ${profile.total_pnl >= 0 ? "text-[#7A9E80]" : "text-[#B56258]"}`} data-testid="profile-total-pnl">{formatMoney(profile.total_pnl, profile.trader.currency)}</p>
              </div>
              <div className="border border-[#2E2A25] p-4 rounded-sm">
                <p className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">Trades</p>
                <p className="font-mono text-xl text-[#EBE5D9] mt-1">{profile.trade_count}</p>
              </div>
              <div className="border border-[#2E2A25] p-4 rounded-sm">
                <p className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">Highest win</p>
                <p className="font-mono text-lg text-[#7A9E80] mt-1">{profile.highest_win ? formatMoney(profile.highest_win.pnl, profile.trader.currency) : "—"}</p>
                {profile.highest_win && <p className="font-mono text-[10px] text-[#A39A8E]">{profile.highest_win.pair}</p>}
              </div>
              <div className="border border-[#2E2A25] p-4 rounded-sm">
                <p className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">Worst loss</p>
                <p className="font-mono text-lg text-[#B56258] mt-1">{profile.worst_loss ? formatMoney(profile.worst_loss.pnl, profile.trader.currency) : "—"}</p>
                {profile.worst_loss && <p className="font-mono text-[10px] text-[#A39A8E]">{profile.worst_loss.pair}</p>}
              </div>
            </div>

            <ActivityStrip activity={profile.activity || []} currency={profile.trader.currency} />
          </>
        )}

        {isOwn && <AccountsSection />}

        {isOwn && (
          <div className="mt-10 border border-[#2E2A25] bg-[#1A1815] p-6 rounded-sm">
            <h2 className="font-head text-2xl text-[#EBE5D9] mb-4">Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8E]">Handle</label>
                <input data-testid="profile-handle" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} className="mt-1 w-full bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-3 py-2 rounded-sm focus:outline-none focus:border-[#CFA66A] font-mono" />
              </div>
            </div>
            <div className="mt-4">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8E]">Bio</label>
              <textarea data-testid="profile-bio" rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1 w-full bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-3 py-2 rounded-sm focus:outline-none focus:border-[#CFA66A] resize-none" />
            </div>
            <label className="flex items-center gap-2 mt-4 text-sm text-[#EBE5D9]">
              <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} data-testid="profile-is-public" />
              Show me on the public leaderboard
            </label>
            {msg && <p className="mt-4 font-mono text-sm text-[#CFA66A]">{msg}</p>}
            <button data-testid="profile-save" onClick={save} disabled={saving} className="mt-4 bg-[#CFA66A] text-[#0F0E0C] font-medium px-6 py-2 rounded-sm hover:bg-[#EBE5D9] transition-colors disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
